import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Lock, Crown } from "lucide-react";
import { toast } from "sonner";
import api, { errText } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CoinIcon } from "@/components/Icons";
import { TierCelebration } from "@/components/TierCelebration";

const fmtINR = (n) => (n >= 100000 ? `₹${(n / 100000).toFixed(n % 100000 === 0 ? 0 : 2)}L` : `₹${n.toLocaleString("en-IN")}`);

// ascending arc positions (percent) for the first 9 gems
const ARC = [
  { l: 1, b: 4 }, { l: 12, b: 10 }, { l: 23, b: 18 }, { l: 34, b: 28 }, { l: 45, b: 40 },
  { l: 56, b: 52 }, { l: 66, b: 62 }, { l: 76, b: 72 }, { l: 85, b: 82 },
];

const BENEFITS = [
  { icon: "💎", label: "Exclusive gem gifts" },
  { icon: "🖼️", label: "Profile aura frame" },
  { icon: "🎙️", label: "Priority mic seat" },
  { icon: "📈", label: "+5% ruby earnings" },
];

function TierRow({ t, current, busy, onBuy, index }) {
  const isCurrent = current?.name === t.name;
  return (
    <motion.div initial={{ opacity: 0, x: -14 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: index * 0.04 }}
      data-testid={`tier-row-${t.name.replace(/ /g, "-")}`}
      className={`relative rounded-[20px] px-3.5 py-3 flex items-center gap-3 border ${isCurrent ? "border-2" : "border-white/[0.06]"} bg-[#14101F]`}
      style={isCurrent ? { borderColor: t.color, boxShadow: `0 0 18px ${t.color}44` } : undefined}>
      <span className="w-6 text-center text-[#6E6683] text-xs font-extrabold">{t.rank}</span>
      {t.image ? (
        <img src={t.image} alt={t.name} loading="lazy"
          className="w-12 h-12 object-contain shrink-0"
          style={{ filter: `drop-shadow(0 0 8px ${t.color}66) drop-shadow(0 2px 4px rgba(0,0,0,0.5))` }} />
      ) : (
        <span className="w-9 h-9 rounded-xl flex items-center justify-center text-lg shrink-0"
          style={{ background: `${t.color}22`, border: `1px solid ${t.color}55` }}>{t.emoji}</span>
      )}
      <div className="min-w-0 flex-1">
        <p className="text-sm font-extrabold flex items-center gap-1.5" style={{ color: t.color }}>
          {t.name}
          {isCurrent && <span className="text-[8px] bg-white/10 text-white px-1.5 py-0.5 rounded-full">CURRENT</span>}
        </p>
        <p className="text-[#6E6683] text-[10px] truncate">{t.meaning}</p>
      </div>
      <div className="text-right shrink-0">
        {t.invitation_only ? (
          <span className="text-[10px] text-[#6E6683] font-bold flex items-center gap-1"><Lock size={10} /> Invitation only</span>
        ) : (
          <>
            <p className="text-[10px] text-[#6E6683]">{fmtINR(t.price_inr)} · 30 days</p>
            <button data-testid={`tier-buy-${t.name.replace(/ /g, "-")}`} onClick={() => onBuy(t)} disabled={busy}
              className={`mt-1 text-[10px] font-extrabold px-3 py-1.5 rounded-full flex items-center gap-1 ml-auto active:scale-95 transition-transform disabled:opacity-50 ${isCurrent ? "bg-white/10 text-white" : "text-black"}`}
              style={isCurrent ? undefined : { background: t.color }}>
              <CoinIcon size={11} /> {t.price_coins?.toLocaleString("en-IN")}{isCurrent ? " · Renew" : ""}
            </button>
          </>
        )}
        {t.requires_lifetime_spend && (
          <p className="text-[8px] text-amber-500/80 mt-0.5 max-w-[110px]">Needs 20-Crown Ribbon (lifetime spend)</p>
        )}
      </div>
    </motion.div>
  );
}

export default function Navaratna() {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const [tiers, setTiers] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [celebrate, setCelebrate] = useState(null);

  useEffect(() => {
    api.get("/tiers").then(({ data }) => setTiers(data.tiers)).catch(() => setTiers([]));
  }, []);

  const current = tiers?.find((t) => t.name === user?.tier) || null;
  const daysLeft = user?.tier_expires_at ? Math.max(0, Math.ceil((new Date(user.tier_expires_at) - Date.now()) / 86400000)) : 0;
  const next = tiers?.find((t) => !t.invitation_only && (!current || t.rank > current.rank) && t.price_coins) || null;
  const coins = user?.coins ?? 0;
  const nextPct = next ? Math.min(100, Math.round((coins / next.price_coins) * 100)) : 0;

  const buy = async (t) => {
    setBusy(true);
    try {
      const { data } = await api.post("/tiers/purchase", { tier: t.name });
      setUser((u) => ({ ...u, ...data.user }));
      setConfirm(null);
      setCelebrate(t);
    } catch (e) {
      toast.error(errText(e));
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen-dvh max-w-md mx-auto pb-10" data-testid="navaratna-page">
      <div className="sticky top-0 z-20 bg-[#0B0B14]/90 backdrop-blur-md px-4 pt-4 pb-3 flex items-center gap-3 border-b border-[rgba(255,216,107,0.12)]">
        <button data-testid="navaratna-back" onClick={() => navigate(-1)} className="text-white/80"><ArrowLeft size={20} /></button>
        <h1 className="font-serif-display text-xl font-bold text-white flex-1 text-center">Navaratna Ladder <span className="text-[#00E0C7]">💠</span></h1>
        <span className="w-5" />
      </div>

      <div className="px-4">
        {/* Hero: ascending gem arc */}
        <div className="relative h-56 mt-3 mb-1" data-testid="navaratna-arc">
          <span className="absolute left-1/4 bottom-1/4 w-52 h-32 rounded-full blur-3xl opacity-40 pointer-events-none rotate-[-24deg]"
            style={{ background: "linear-gradient(60deg, rgba(255,46,151,0.5), rgba(255,216,107,0.35), transparent)" }} />
          {(tiers || []).slice(0, 9).map((t, i) => {
            const pos = ARC[i];
            const isCur = current?.name === t.name;
            return (
              <span key={t.name} className="absolute" style={{ left: `${pos.l}%`, bottom: `${pos.b}%` }}>
                {isCur && <Crown size={14} className="absolute -top-5 left-1/2 -translate-x-1/2 text-[#FFD86B] drop-shadow-[0_0_6px_rgba(255,216,107,0.9)]" />}
                <span className={`block rounded-full ${isCur ? "p-[3px]" : ""}`}
                  style={isCur ? { background: "conic-gradient(from 200deg, #FF2E97, #A03CF5, #FFD86B, #FF2E97)", boxShadow: `0 0 22px ${t.color}88` } : undefined}>
                  {t.image ? (
                    <img src={t.image} alt={t.name} loading="lazy"
                      className={`object-contain ${isCur ? "w-14 h-14" : "w-10 h-10"}`}
                      style={{ filter: `drop-shadow(0 0 ${isCur ? 14 : 8}px ${t.color}${isCur ? "AA" : "66"})` }} />
                  ) : (
                    <span className="text-2xl">{t.emoji}</span>
                  )}
                </span>
              </span>
            );
          })}
        </div>

        <p className="text-center text-white text-lg font-bold" data-testid="current-tier-card">
          {current ? <>{current.name} <span className="text-[#6E6683] font-semibold">· Tier {current.rank}</span></> : <span className="text-[#B8B0C9]">Climb the nine gems & beyond</span>}
        </p>
        {current && (
          <p className="text-center text-[#6E6683] text-[11px] mt-0.5">{daysLeft} day{daysLeft === 1 ? "" : "s"} remaining · renews 30 days per purchase</p>
        )}

        {next && (
          <div className="mt-4" data-testid="navaratna-progress">
            <p className="text-center text-[13px] mb-2">
              <span className="text-[#FF4D8D] font-extrabold">{coins.toLocaleString("en-IN")}</span>
              <span className="text-[#6E6683]"> / {next.price_coins.toLocaleString("en-IN")} coins to {next.name}</span>
            </p>
            <div className="h-2 rounded-full bg-white/[0.07] overflow-hidden">
              <div className="h-full rounded-full transition-all duration-700" style={{ width: `${Math.max(3, nextPct)}%`, background: "linear-gradient(90deg, #FF4D8D, #FF2E97)" }} />
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-2.5 mt-5" data-testid="navaratna-benefits">
          {BENEFITS.map((b) => (
            <div key={b.label} className="bg-[#14101F] border border-white/[0.06] rounded-[20px] px-3.5 py-3.5 flex items-center gap-2.5">
              <span className="w-9 h-9 rounded-2xl flex items-center justify-center text-base shrink-0 gold-hairline"
                style={{ background: "radial-gradient(circle at 30% 25%, rgba(255,216,107,0.14), rgba(30,24,48,0.9))" }}>{b.icon}</span>
              <p className="text-white text-xs font-bold leading-snug">{b.label}</p>
            </div>
          ))}
        </div>

        <button data-testid="navaratna-vip-band" onClick={() => navigate("/vip")}
          className="w-full rounded-2xl px-4 py-3.5 mt-4 mb-6 flex items-center justify-between active:scale-[0.98] transition-transform"
          style={{ background: "rgba(245,166,35,0.15)", border: "1px solid rgba(245,166,35,0.4)" }}>
          <p className="text-left text-[#F5A623] font-bold text-xs">👑 Yaara VIP — from ₹299/mo · <span className="text-white/90">+10% bonus coins · VIP badge in chat</span></p>
          <span className="bg-[#FFD86B] text-[#0B0B14] text-[10px] font-extrabold px-3 py-1.5 rounded-full shrink-0 ml-2">Upgrade</span>
        </button>

        <h2 className="text-white text-sm font-bold mb-2.5">All Levels</h2>
        <div className="space-y-2">
          {tiers === null && [1, 2, 3, 4, 5].map((i) => <div key={i} className="skeleton h-16 rounded-2xl" />)}
          {tiers?.map((t, i) => (
            <TierRow key={t.name} t={t} current={current} busy={busy} index={i}
              onBuy={(x) => ((user?.coins ?? 0) < (x.price_coins || 0)
                ? toast.error(`Not enough coins — you need ${(x.price_coins - (user?.coins || 0)).toLocaleString("en-IN")} more`)
                : setConfirm(x))} />
          ))}
        </div>
      </div>

      {confirm && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6" onClick={() => setConfirm(null)}>
          <div className="sheet-luxe !rounded-3xl bg-[#14101F] p-5 w-full max-w-xs text-center" onClick={(e) => e.stopPropagation()} data-testid="tier-confirm-modal">
            {confirm.image ? (
              <img src={confirm.image} alt={confirm.name} className="w-24 h-24 object-contain mx-auto"
                style={{ filter: `drop-shadow(0 0 14px ${confirm.color}88)` }} />
            ) : (
              <span className="text-4xl">{confirm.emoji}</span>
            )}
            <p className="font-extrabold mt-2" style={{ color: confirm.color }}>Become {confirm.name}?</p>
            <p className="text-[#B8B0C9] text-[11px] mt-1 flex items-center justify-center gap-1"><CoinIcon size={12} /> {confirm.price_coins?.toLocaleString("en-IN")} coins · valid 30 days</p>
            <div className="flex gap-2 mt-4">
              <button data-testid="tier-confirm-cancel" onClick={() => setConfirm(null)} className="flex-1 bg-white/10 text-[#B8B0C9] text-xs font-bold py-2.5 rounded-full">Cancel</button>
              <button data-testid="tier-confirm-buy" onClick={() => buy(confirm)} disabled={busy}
                className="flex-1 text-black text-xs font-extrabold py-2.5 rounded-full disabled:opacity-50" style={{ background: confirm.color }}>
                {busy ? "Buying…" : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}

      {celebrate && <TierCelebration tier={celebrate} onClose={() => setCelebrate(null)} />}
    </div>
  );
}
